/**
 * Gateway Server - WebSocket and HTTP server for OpenClaw
 *
 * Handles:
 * - WebSocket connections from clients, nodes, and channels
 * - HTTP API for configuration and management
 * - Static file serving for web UI
 * - Message routing between channels and agents
 */

import * as fs from "node:fs/promises";
import * as http from "node:http";
import * as path from "node:path";
import { WebSocket, WebSocketServer } from "ws";
import type { ConfigManager } from "../config/manager.js";
import { type ChannelAdapter, globalChannelRegistry } from "./channels/base.js";
import { MessageRouter } from "./router.js";
import type { SessionManager } from "./sessions.js";
import type {
	ConnectionType,
	GatewayConfig,
	GatewayEventMap,
	GatewayServer,
	InboundMessage,
	OutboundMessage,
	ServerStatus,
	WebSocketConnection,
} from "./types/index.js";

/** Gateway options */
export interface GatewayOptions {
	configManager: ConfigManager;
	sessionManager: SessionManager;
}

/** Gateway implementation */
export class Gateway implements GatewayServer {
	private configManager: ConfigManager;
	private sessionManager: SessionManager;
	private router: MessageRouter;
	private config: GatewayConfig;
	private httpServer?: http.Server;
	private wsServer?: WebSocketServer;
	private connections = new Map<string, WebSocketConnectionImpl>();
	private channelAdapters = new Map<string, ChannelAdapter>();
	private eventListeners = new Map<keyof GatewayEventMap, Array<(data: unknown) => void>>();
	private startTime?: Date;
	private connectionIdCounter = 0;

	constructor(options: GatewayOptions) {
		this.configManager = options.configManager;
		this.sessionManager = options.sessionManager;
		this.router = new MessageRouter({
			configManager: options.configManager,
			sessionManager: options.sessionManager,
		});
		this.config = options.configManager.getGatewayConfig();
	}

	/** Start the gateway server */
	async start(): Promise<void> {
		// Initialize channel adapters
		await this.initializeChannels();

		// Create HTTP server
		this.httpServer = http.createServer(this.handleHttpRequest.bind(this));

		// Create WebSocket server
		this.wsServer = new WebSocketServer({
			noServer: true,
			maxPayload: 10 * 1024 * 1024, // 10MB
		});

		// Handle WebSocket connections
		this.wsServer.on("connection", this.handleWsConnection.bind(this));

		// Handle upgrade from HTTP to WebSocket
		this.httpServer.on("upgrade", this.handleUpgrade.bind(this));

		// Start listening
		await new Promise<void>((resolve, reject) => {
			this.httpServer!.listen(this.config.port, () => {
				this.startTime = new Date();
				console.log(`OpenClaw Gateway listening on port ${this.config.port}`);
				resolve();
			});
			this.httpServer!.on("error", reject);
		});

		// Start cleanup interval
		this.startCleanupInterval();
	}

	/** Stop the gateway server */
	async stop(): Promise<void> {
		// Close all connections
		for (const connection of this.connections.values()) {
			connection.close();
		}
		this.connections.clear();

		// Shutdown channel adapters
		for (const adapter of this.channelAdapters.values()) {
			await adapter.shutdown();
		}
		this.channelAdapters.clear();

		// Close WebSocket server
		this.wsServer?.close();

		// Close HTTP server
		await new Promise<void>((resolve) => {
			this.httpServer?.close(() => resolve());
		});

		console.log("OpenClaw Gateway stopped");
	}

	/** Get server status */
	getStatus(): ServerStatus {
		return {
			running: !!this.startTime,
			port: this.config.port,
			startTime: this.startTime,
			connections: this.connections.size,
			sessions: this.sessionManager.getStats().total,
		};
	}

	/** Initialize channel adapters */
	private async initializeChannels(): Promise<void> {
		const channels = this.configManager.getEnabledChannels();

		for (const channelConfig of channels) {
			try {
				if (globalChannelRegistry.has(channelConfig.type)) {
					const adapter = globalChannelRegistry.create(channelConfig.type, channelConfig);

					// Set up message handler
					adapter.onMessage((message) => {
						this.handleChannelMessage(message);
					});

					// Set up error handler
					adapter.onError((error) => {
						console.error(`Channel ${channelConfig.type} error:`, error);
						this.emit("error", { source: channelConfig.type, error });
					});

					await adapter.initialize();
					this.channelAdapters.set(channelConfig.type, adapter);
					console.log(`Channel ${channelConfig.type} initialized`);
				} else {
					console.warn(`Unknown channel type: ${channelConfig.type}`);
				}
			} catch (error) {
				console.error(`Failed to initialize channel ${channelConfig.type}:`, error);
			}
		}
	}

	/** Handle incoming channel message */
	private async handleChannelMessage(message: InboundMessage): Promise<void> {
		// Emit event
		this.emit("message:inbound", message);

		// Check access
		const access = await this.router.canProcess(message);
		if (!access.allowed) {
			console.log(`Message rejected: ${access.reason}`);
			return;
		}

		// Resolve route
		const route = await this.router.resolveRoute(message);
		if (!route) {
			console.log("No route found for message");
			return;
		}

		// Get or create session
		const session = await this.router.getOrCreateSession(message);

		// Update session state
		this.sessionManager.setSessionState(session.key, "active");

		// Emit session event
		this.emit("session:update", session);

		// TODO: Forward to agent runner
		// For now, just echo back a response
		await this.sendResponse(message, route);
	}

	/** Send response (placeholder) */
	private async sendResponse(
		inbound: InboundMessage,
		route: { agentId: string; channelConfig: { type: string } },
	): Promise<void> {
		const adapter = this.channelAdapters.get(route.channelConfig.type);
		if (!adapter) return;

		// Create outbound message
		const outbound: OutboundMessage = {
			id: this.generateId(),
			sessionKey: inbound.sessionKey,
			channel: route.channelConfig.type,
			target: inbound.sender.id,
			parts: [
				{
					type: "text",
					text: `Received your message: "${(inbound.content as { text: string }).text ?? "(no text)"}"`,
				},
			],
			replyTo: inbound.id,
		};

		await adapter.send(outbound);
		this.emit("message:outbound", outbound);
	}

	/** Handle HTTP request */
	private async handleHttpRequest(req: http.IncomingMessage, res: http.ServerResponse): Promise<void> {
		const url = new URL(req.url ?? "/", `http://${req.headers.host}`);

		// CORS headers
		this.setCorsHeaders(res);

		if (req.method === "OPTIONS") {
			res.writeHead(200);
			res.end();
			return;
		}

		// API routes
		if (url.pathname.startsWith("/api/")) {
			await this.handleApiRequest(req, res, url);
			return;
		}

		// Static files
		await this.handleStaticFile(req, res, url);
	}

	/** Set CORS headers */
	private setCorsHeaders(res: http.ServerResponse): void {
		const origins = this.config.corsOrigins;
		if (origins.includes("*")) {
			res.setHeader("Access-Control-Allow-Origin", "*");
		}
		res.setHeader("Access-Control-Allow-Methods", "GET, POST, PUT, DELETE, OPTIONS");
		res.setHeader("Access-Control-Allow-Headers", "Content-Type, Authorization");
	}

	/** Handle API request */
	private async handleApiRequest(req: http.IncomingMessage, res: http.ServerResponse, url: URL): Promise<void> {
		const path = url.pathname.slice(5); // Remove /api/

		try {
			switch (path) {
				case "status":
					this.jsonResponse(res, this.getStatus());
					break;

				case "config":
					if (req.method === "GET") {
						this.jsonResponse(res, this.configManager.getConfig());
					} else if (req.method === "POST") {
						// Update config
						const body = await this.readBody(req);
						const updates = JSON.parse(body);
						this.configManager.updateConfig(updates);
						await this.configManager.save();
						this.jsonResponse(res, { success: true });
					} else {
						this.errorResponse(res, 405, "Method not allowed");
					}
					break;

				case "sessions":
					if (req.method === "GET") {
						this.jsonResponse(res, this.sessionManager.getStats());
					} else {
						this.errorResponse(res, 405, "Method not allowed");
					}
					break;

				case "channels":
					if (req.method === "GET") {
						const channels = this.configManager.getEnabledChannels().map((c) => ({
							type: c.type,
							name: c.name,
							enabled: c.enabled,
							connected: this.channelAdapters.get(c.type)?.isConnected() ?? false,
						}));
						this.jsonResponse(res, channels);
					} else {
						this.errorResponse(res, 405, "Method not allowed");
					}
					break;

				default:
					this.errorResponse(res, 404, "Not found");
			}
		} catch (error) {
			console.error("API error:", error);
			this.errorResponse(res, 500, "Internal server error");
		}
	}

	/** Handle static file request */
	private async handleStaticFile(req: http.IncomingMessage, res: http.ServerResponse, url: URL): Promise<void> {
		// Default to index.html
		let filePath = url.pathname === "/" ? "index.html" : url.pathname.slice(1);

		// Security: prevent directory traversal
		filePath = path.normalize(filePath).replace(/^(\.\.[/\\])+/, "");

		const fullPath = path.join(this.config.publicDir, filePath);

		try {
			const content = await fs.readFile(fullPath);
			const ext = path.extname(fullPath);
			const contentType = this.getContentType(ext);

			res.setHeader("Content-Type", contentType);
			res.writeHead(200);
			res.end(content);
		} catch {
			// Try to serve index.html for SPA routes
			try {
				const indexPath = path.join(this.config.publicDir, "index.html");
				const content = await fs.readFile(indexPath);
				res.setHeader("Content-Type", "text/html");
				res.writeHead(200);
				res.end(content);
			} catch {
				this.errorResponse(res, 404, "Not found");
			}
		}
	}

	/** Get content type by extension */
	private getContentType(ext: string): string {
		const types: Record<string, string> = {
			".html": "text/html",
			".js": "application/javascript",
			".css": "text/css",
			".json": "application/json",
			".png": "image/png",
			".jpg": "image/jpeg",
			".gif": "image/gif",
			".svg": "image/svg+xml",
			".ico": "image/x-icon",
		};
		return types[ext] || "application/octet-stream";
	}

	/** Handle HTTP upgrade to WebSocket */
	private handleUpgrade(req: http.IncomingMessage, socket: any, head: Buffer): void {
		const upgrade = req.headers.upgrade?.toLowerCase();

		if (upgrade === "websocket") {
			// Check if it's a WebSocket path
			const url = new URL(req.url ?? "/", `http://${req.headers.host}`);
			if (url.pathname === this.config.wsPath) {
				this.wsServer?.handleUpgrade(req, socket, head, (ws) => {
					this.wsServer?.emit("connection", ws, req);
				});
			} else {
				socket.destroy();
			}
		}
	}

	/** Handle WebSocket connection */
	private handleWsConnection(ws: WebSocket, req: http.IncomingMessage): void {
		const connectionId = this.generateConnectionId();
		const url = new URL(req.url ?? "/", `http://${req.headers.host}`);

		// Determine connection type from query params
		const type = (url.searchParams.get("type") as ConnectionType) || "client";
		const agentId = url.searchParams.get("agent") || undefined;
		const sessionKey = url.searchParams.get("session") || undefined;

		const connection = new WebSocketConnectionImpl({
			id: connectionId,
			type,
			agentId,
			sessionKey,
			ws,
		});

		this.connections.set(connectionId, connection);

		// Handle messages
		ws.on("message", (data) => {
			this.handleWsMessage(connection, data);
		});

		// Handle close
		ws.on("close", () => {
			this.connections.delete(connectionId);
			this.emit("connection:close", {
				connectionId,
				reason: "closed",
			});
		});

		// Handle errors
		ws.on("error", (error) => {
			console.error(`WebSocket error on ${connectionId}:`, error);
			this.emit("error", { source: connectionId, error });
		});

		this.emit("connection:open", { connectionId, type });

		// Send welcome
		connection.send({
			type: "connected",
			connectionId,
			serverTime: new Date().toISOString(),
		});
	}

	/** Handle WebSocket message */
	private handleWsMessage(connection: WebSocketConnectionImpl, data: Buffer | ArrayBuffer | Buffer[]): void {
		try {
			const message = JSON.parse(data.toString());

			// Handle different message types
			switch (message.type) {
				case "ping":
					connection.send({ type: "pong", time: Date.now() });
					break;

				case "subscribe":
					// Subscribe to session events
					if (message.sessionKey) {
						connection.sessionKey = message.sessionKey;
					}
					break;

				case "message":
					// Handle outgoing message from client
					// TODO: Process through agent
					break;

				default:
					console.log(`Unknown message type: ${message.type}`);
			}
		} catch (error) {
			console.error("Failed to parse WebSocket message:", error);
		}
	}

	/** Generate unique connection ID */
	private generateConnectionId(): string {
		return `conn-${Date.now()}-${++this.connectionIdCounter}`;
	}

	/** Generate unique message ID */
	private generateId(): string {
		return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
	}

	/** Read request body */
	private readBody(req: http.IncomingMessage): Promise<string> {
		return new Promise((resolve, reject) => {
			let body = "";
			req.on("data", (chunk) => {
				body += chunk.toString();
			});
			req.on("end", () => resolve(body));
			req.on("error", reject);
		});
	}

	/** Send JSON response */
	private jsonResponse(res: http.ServerResponse, data: unknown): void {
		res.setHeader("Content-Type", "application/json");
		res.writeHead(200);
		res.end(JSON.stringify(data));
	}

	/** Send error response */
	private errorResponse(res: http.ServerResponse, code: number, message: string): void {
		res.setHeader("Content-Type", "application/json");
		res.writeHead(code);
		res.end(JSON.stringify({ error: message }));
	}

	/** Start cleanup interval */
	private startCleanupInterval(): void {
		setInterval(async () => {
			const closed = await this.sessionManager.cleanup();
			if (closed > 0) {
				console.log(`Cleaned up ${closed} idle sessions`);
			}
		}, 60000); // Every minute
	}

	/** Add event listener */
	on<T extends keyof GatewayEventMap>(event: T, listener: (data: GatewayEventMap[T]) => void): void {
		if (!this.eventListeners.has(event)) {
			this.eventListeners.set(event, []);
		}
		this.eventListeners.get(event)!.push(listener as (data: unknown) => void);
	}

	/** Remove event listener */
	off<T extends keyof GatewayEventMap>(event: T, listener: (data: GatewayEventMap[T]) => void): void {
		const listeners = this.eventListeners.get(event);
		if (listeners) {
			const index = listeners.indexOf(listener as (data: unknown) => void);
			if (index >= 0) {
				listeners.splice(index, 1);
			}
		}
	}

	/** Emit event */
	private emit<T extends keyof GatewayEventMap>(event: T, data: GatewayEventMap[T]): void {
		const listeners = this.eventListeners.get(event);
		if (listeners) {
			for (const listener of listeners) {
				try {
					listener(data);
				} catch (error) {
					console.error(`Error in event listener for ${event}:`, error);
				}
			}
		}
	}
}

/** WebSocket connection implementation */
class WebSocketConnectionImpl implements WebSocketConnection {
	id: string;
	type: ConnectionType;
	agentId?: string;
	sessionKey?: string;
	ws: WebSocket;
	isAlive = true;
	lastActivity: Date;

	constructor(params: {
		id: string;
		type: ConnectionType;
		agentId?: string;
		sessionKey?: string;
		ws: WebSocket;
	}) {
		this.id = params.id;
		this.type = params.type;
		this.agentId = params.agentId;
		this.sessionKey = params.sessionKey;
		this.ws = params.ws;
		this.lastActivity = new Date();
	}

	send(data: unknown): void {
		if (this.ws.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(data));
			this.lastActivity = new Date();
		}
	}

	close(): void {
		this.ws.close();
		this.isAlive = false;
	}
}
